import classNames from 'classnames';
import {observer, Provider} from 'mobx-react';
import React, {useCallback, useEffect, useRef} from 'react';

import {
    AsyncPriceCourseCardContext,
    CourseProgressCardsSkeleton,
    BrowseCourse,
} from '@udemy/browse-course';
import {
    UI_REGION,
    UpdateCareerGoalSelected,
    GoalCollectionTargetPage,
    discoveryTracker,
    BackendSourceOptions,
} from '@udemy/browse-event-tracking';
import {GwgAccelerator} from '@udemy/career-academies';
import {DiscoveryUnit, DiscoveryUnitItem, PAGE_TYPE_LOGGED_IN_HOMEPAGE} from '@udemy/discovery-api';
import {Tracker, TrackImpression, TrackingContextProvider} from '@udemy/event-tracking';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {BadgeClass, FeatureVariantAssignment} from '@udemy/graphql';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {
    CertificationBundle,
    CertificationPrepRecoBanner,
    type RecommendedCertificationVoucherExam,
} from '@udemy/react-certification-preparation';
import {
    CourseUnit,
    DiscoveryUnitsContainer,
    PopularTopicsUnit,
    SingleCourseUnit,
    SkillsHubCourseUnit,
    ValueProps,
    PopularTopicsDiscoveryItem,
} from '@udemy/react-discovery-units';
import {ExperimentImpressionEvent, useFeatures} from '@udemy/react-experimentation-provider';
import {
    CourseGuidanceEntryPointWithPreselection,
    EntryPointPopUp,
    TOP_10_COURSES_IN_COUNTRY_VIEW_TYPE,
    Top10CoursesComponent,
    TopCategoriesComponent,
} from '@udemy/react-globalization-components';
import {
    OnePaneLayoutContainer,
    OnePaneLayoutFullWidthSection,
} from '@udemy/react-layout-components';
import {LectureProgressTab, MyLearningUnitTabProvider} from '@udemy/react-my-learning-unit';
import {
    PERSONALIZE_URL,
    UserOccupationGoalHeader,
    UserOccupationInfo,
} from '@udemy/react-personalized-content';
import {TextSkeleton} from '@udemy/react-reveal-components';
import {StreaksBox, StreakUserLectureConsumptionWeeklyStore} from '@udemy/react-student-retention';
import {useUDData} from '@udemy/ud-data';

import {MyLearningUnitContainer} from 'src/components/my-learning-unit-container/my-learning-unit-container.react-component';
import {RetentionPromoClaimAlert} from 'src/components/retention-promo-claim-alert/retention-promo-claim-alert.react-component';
import {CertificationVoucherExam} from 'src/generated/graphql';
import {
    LOCALE_SPECIFIC_TOPICS,
    COURSE_GUIDANCE_FEATURE,
    USER_STREAKS_ON_MX_LIHP,
    LIHP_CERT_VOUCHER_RECO_BANNER,
    TOP_10_COURSES_JAPAN,
    PP_MODULE_POSITIONING,
    LIHP_CAROUSEL_HOVER_POPUP_REMOVAL,
    POPULAR_CATEGORIES_MODULE,
    LIHP_MARKETING_MESSAGING_MODULES,
} from 'src/lib/experimentation-constants';
import {
    type CategoryUniqueLearnerStatsMap,
    getCategoryUniqueLearnerStatsForCountry,
} from 'src/lib/fetchers';
import {HomeContextData} from 'src/types/home-context';
import {UserEnrollmentsData} from 'src/types/page-props';
import {getCoursesData} from 'src/utils/get-courses-data';
import {personalizeDiscoveryUnitPropData} from 'src/utils/personalize-course-unit-props-helper';

import {
    COURSE_RECOMMENDATIONS_SECTION_ID,
    FUNNEL_LOG_PAGE_TYPE,
    MY_COURSES_HOME_URL,
} from '../constants';
import {GwgSection} from './gwg-section.react-component';
import styles from './home-page.module.less';
import {MessagingModuleSlot} from './messaging-modules/messaging-module-slot.react-component';
import {NextTopicsUnit} from './next-topics-unit.react-component';
import {PauseSubscriptionBanner} from './pause-subscription-banner/pause-subscription-banner.react-component';
import {usePauseSubscriptionEnrollment} from './pause-subscription-banner/use-pause-subscription-enrollment';
import {PersonalPlanUpsellBanner} from './personal-plan-upsell/personal-plan-upsell-banner.react.component';
import {PpBanner, PpBannerVariant} from './pp-banner.react-component';

interface HomePageFeatureProps {
    contextData: HomeContextData;
    userOccupationInfo: UserOccupationInfo;
    hasSeenUserGoalHeader: boolean;
    userEnrollments?: UserEnrollmentsData;
    accelerator?: GwgAccelerator;
    recommendedExam?: CertificationVoucherExam;
    topicSlug?: string;
    bundle?: CertificationBundle;
    certification?: BadgeClass;
    categoryUniqueLearnerStatsMap?: CategoryUniqueLearnerStatsMap;
}

export const HomePageFeature = observer((props: HomePageFeatureProps) => {
    const {gettext} = useI18n();
    const isMobileMax = useMatchMedia('mobile-max');
    const [streaksStore] = React.useState(() => new StreakUserLectureConsumptionWeeklyStore());
    const [showUserStreaks, setShowUserStreaks] = React.useState(false);
    const udData = useUDData();
    const pauseSubscriptionEnrollment = usePauseSubscriptionEnrollment();
    const isPriceCountryJapan = udData.Config.price_country?.id === 'JP';

    const {featureVariantAssignments} = useFeatures([
        LOCALE_SPECIFIC_TOPICS,
        COURSE_GUIDANCE_FEATURE,
        USER_STREAKS_ON_MX_LIHP,
        LIHP_CERT_VOUCHER_RECO_BANNER,
        TOP_10_COURSES_JAPAN,
        PP_MODULE_POSITIONING,
        LIHP_CAROUSEL_HOVER_POPUP_REMOVAL,
        POPULAR_CATEGORIES_MODULE,
        LIHP_MARKETING_MESSAGING_MODULES,
    ]);

    const localeSpecificTopicsFeature =
        featureVariantAssignments.get(LOCALE_SPECIFIC_TOPICS)?.configuration ?? {};
    const showCourseGuidanceFeature =
        featureVariantAssignments.get(COURSE_GUIDANCE_FEATURE)?.configuration ?? {};

    const userStreakOnMXLIHPExperiment = featureVariantAssignments.get(USER_STREAKS_ON_MX_LIHP);
    const lihpCertVoucherRecoBannerExperiment = featureVariantAssignments.get(
        LIHP_CERT_VOUCHER_RECO_BANNER,
    );
    const top10CoursesJapanExperiment = featureVariantAssignments.get(TOP_10_COURSES_JAPAN);
    const ppModulePositioningExperiment = featureVariantAssignments.get(PP_MODULE_POSITIONING);
    const {variant1: showPpBannerA = false, variant2: showPpBannerB = false} =
        ppModulePositioningExperiment?.configuration ?? {};
    const ppBannerVariant: PpBannerVariant | undefined = showPpBannerA
        ? 'bannerVariantA'
        : showPpBannerB
          ? 'bannerVariantB'
          : undefined;

    // CVR-2667. Independent of pp_module_positioning above (which is from an archived
    // experiment per Product) — this does not read or gate on it.
    const lihpMarketingMessagingModulesExperiment = featureVariantAssignments.get(
        LIHP_MARKETING_MESSAGING_MODULES,
    );
    const messagingModuleVariant: 'control' | 'bento' | 'tabs' | undefined =
        lihpMarketingMessagingModulesExperiment?.configuration?.variant;

    // Named explicitly, not left as an inline condition in the JSX below — a plain-
    // truthiness version of this exact check (`ppModulePositioningExperiment` instead of
    // `.isInExperiment`)
    const showPpModuleOverMessagingModule = ppModulePositioningExperiment?.isInExperiment;

    // TODO: clean up once lihp_carousel_hover_popup_removal experiment concludes
    const carouselHoverPopupRemovalExperiment = featureVariantAssignments.get(
        LIHP_CAROUSEL_HOVER_POPUP_REMOVAL,
    );
    const isCarouselHoverPopupRemovalActive = Boolean(
        carouselHoverPopupRemovalExperiment?.configuration?.enabled,
    );
    const showStateAwareCtaInCourseCard = isCarouselHoverPopupRemovalActive && !isMobileMax;

    const popularCategoriesModuleFeature =
        featureVariantAssignments.get(POPULAR_CATEGORIES_MODULE)?.configuration ?? {};
    // TopCategoriesComponent falls back to its hardcoded learner counts when the stats
    // are null, so the control group keeps the counts it renders today.
    const dynamicCategoryUniqueLearnerStats =
        popularCategoriesModuleFeature.show_dynamic_popular_categories_module
            ? getCategoryUniqueLearnerStatsForCountry(
                  props.categoryUniqueLearnerStatsMap,
                  udData.Config.price_country?.id ?? '',
              )
            : null;
    const learningTab = useRef(
        new LectureProgressTab(
            0,
            'Courses',
            <CourseProgressCardsSkeleton />,
            gettext,
            undefined,
            true,
        ),
    );

    const triggerImpressionEvent = (feature: FeatureVariantAssignment | undefined) => {
        if (!feature) return;
        const {experimentIds, isLocalDefault} = feature;
        Tracker.publishEvent(new ExperimentImpressionEvent(experimentIds, isLocalDefault));
    };

    // Desktop only: the hover popup doesn't exist on mobile, so mobile users are never exposed to
    // either variant and shouldn't count toward the experiment. useMatchMedia returns null until
    // the viewport resolves, so this waits for an explicit false rather than any falsy value.
    // TODO: clean up once lihp_carousel_hover_popup_removal experiment concludes
    useEffect(() => {
        if (isMobileMax !== false) return;
        triggerImpressionEvent(carouselHoverPopupRemovalExperiment);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobileMax, carouselHoverPopupRemovalExperiment]);

    const handlePpModuleImpression = useCallback(() => {
        triggerImpressionEvent(ppModulePositioningExperiment);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppModulePositioningExperiment]);

    const handleMessagingModuleImpression = useCallback(() => {
        triggerImpressionEvent(lihpMarketingMessagingModulesExperiment);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lihpMarketingMessagingModulesExperiment]);

    const getUnitComponentAndProps = (
        unit: Partial<DiscoveryUnit<DiscoveryUnitItem>> | undefined,
    ) => {
        if (!unit) throw new Error('Missing unit');

        const responsiveUnit = {
            fullWidth: isMobileMax,
            showPager: !isMobileMax,
            layout: isMobileMax ? 'multirow' : 'singlerow',
            showAddToCartInCourseCard: false,
            showQuickViewBox: !showStateAwareCtaInCourseCard,
            showStateAwareCtaInCourseCard,
            showInstructorDetails: true,
        };

        switch (unit.type) {
            case 'bestseller_labels':
                return {
                    component: PopularTopicsUnit,
                    contextObj: {
                        subcontext: 'affinity',
                    },
                    componentProps: {
                        trackImpression: (unit: DiscoveryUnit<PopularTopicsDiscoveryItem>) => {
                            discoveryTracker.trackUnitView(
                                {tracking_id: unit.tracking_id ?? '', title: unit.title ?? ''},
                                unit.item_type ?? '',
                            );
                        },
                        trackTopicClick: (topicId: string, topicName: string) => {
                            discoveryTracker.trackDiscoveryItemClickEvent(
                                {
                                    item: {
                                        id: Number(topicId),
                                        title: topicName,
                                        _class: 'course_topic',
                                        type: 'course_topic',
                                        tracking_id: topicId,
                                    },
                                },
                                {
                                    backendSource: BackendSourceOptions.DISCOVERY,
                                },
                            );
                        },
                    },
                };
            case 'top_pick':
            case 'featured_course':
                return {
                    component: SingleCourseUnit,
                    contextObject: {
                        context2: 'featured',
                        subcontext: unit.title,
                        subcontext2: unit.id,
                    },
                };
            case 'discovery_value_props':
                return {
                    component: ValueProps,
                };
            // `NextTopicsUnit` has to do the tab mapping itself: `DiscoveryUnitRenderer` applies
            // `unit` after `componentProps`, so a unit patched here would be overwritten.
            case 'next_topics':
                return {
                    component: NextTopicsUnit,
                    componentProps: {
                        ...responsiveUnit,
                    },
                };
            default:
                switch (unit.view_type) {
                    case 'horizontal_tabbed':
                        return {
                            component: SkillsHubCourseUnit,
                            componentProps: {
                                ...responsiveUnit,
                            },
                        };
                    case 'occupation':
                        return {
                            component: CourseUnit,
                            componentProps: {
                                ...responsiveUnit,
                                ...personalizeDiscoveryUnitPropData(
                                    gettext('Edit occupation'),
                                    `${PERSONALIZE_URL}/occupation`,
                                    new UpdateCareerGoalSelected(
                                        GoalCollectionTargetPage.OCCUPATION,
                                        UI_REGION.COURSE_RECOMMENDATION_UNIT,
                                    ),
                                    true,
                                    true,
                                ),
                                carouselProps: {
                                    ariaLabel: unit.title,
                                    ariaRoleDescription: 'carousel',
                                },
                            },
                        };
                    default:
                        return {
                            component: CourseUnit,
                            componentProps: {
                                ...responsiveUnit,
                                carouselProps: {
                                    ariaLabel: unit.title,
                                    ariaRoleDescription: 'carousel',
                                },
                            },
                            contextObject: {
                                context2: 'featured',
                                subcontext: unit.title,
                                subcontext2: unit.id,
                            },
                        };
                }
        }
    };

    const [voucherRecoBundle, setVoucherRecoBundle] = React.useState<CertificationBundle | null>(
        props.bundle ?? null,
    );

    useEffect(() => {
        const hydrateVoucherBundle = async () => {
            if (!props.bundle) {
                setVoucherRecoBundle(null);
                return;
            }

            const courseIds = props.bundle.contents.map((content) => content.id);
            if (!courseIds.length) {
                setVoucherRecoBundle(props.bundle);
                return;
            }

            const coursesData = await getCoursesData(courseIds, ['objectives_summary']);

            const validCourses = (coursesData ?? []).filter(
                (course): course is BrowseCourse => !!course && 'id' in course && !!course.id,
            );

            setVoucherRecoBundle({
                ...props.bundle,
                contents: validCourses,
            });
        };

        void hydrateVoucherBundle();
    }, [props.bundle]);

    const renderCertificationVoucherRecoBanner = () => {
        const {topicSlug, certification, recommendedExam} = props;
        const bundle = voucherRecoBundle;
        const showCertificationVoucherRecoBannerExperiment =
            lihpCertVoucherRecoBannerExperiment?.configuration?.showBanner ?? false;

        triggerImpressionEvent(lihpCertVoucherRecoBannerExperiment);
        if (!showCertificationVoucherRecoBannerExperiment) return null;

        if (
            !topicSlug ||
            !bundle ||
            !bundle.contents?.length ||
            !certification ||
            !recommendedExam
        ) {
            return null;
        }

        return (
            <div className={classNames('component-margin')}>
                <FunnelLogContextProvider pageType={PAGE_TYPE_LOGGED_IN_HOMEPAGE}>
                    <TrackingContextProvider
                        trackingContext={{
                            trackImpressionFunc: discoveryTracker.trackDiscoveryImpression,
                            backendSource: BackendSourceOptions.DISCOVERY,
                        }}
                    >
                        <CertificationPrepRecoBanner
                            topicSlug={topicSlug}
                            certificationBundleCourses={bundle.contents as BrowseCourse[]}
                            certification={certification as BadgeClass}
                            recommendedExam={recommendedExam as RecommendedCertificationVoucherExam}
                        />
                    </TrackingContextProvider>
                </FunnelLogContextProvider>
            </div>
        );
    };

    const discoveryContainerFilter = (u: Partial<DiscoveryUnit<DiscoveryUnitItem>>) =>
        u.view_type !== TOP_10_COURSES_IN_COUNTRY_VIEW_TYPE && !!u.items?.length;

    const renderDiscoveryUnits = () => {
        const itemIndices = localeSpecificTopicsFeature.show_locale_specific_topic
            ? {bestseller_labels: 0}
            : {};
        return (
            <>
                <Provider
                    showPersonalPlanBadge={props.contextData?.showPersonalPlanBadge}
                    showCodingExercisesBadge={props.contextData?.showCodingExercisesBadge}
                >
                    <AsyncPriceCourseCardContext.Provider
                        value={{
                            useCourseProductCard: showStateAwareCtaInCourseCard,
                            hideCourseDescription: true,
                        }}
                    >
                        <DiscoveryUnitsContainer
                            belowFirstUnit={
                                showPpModuleOverMessagingModule ? (
                                    <TrackImpression
                                        trackFunc={handlePpModuleImpression}
                                        visibilityThreshold={0}
                                    >
                                        <div
                                            data-purpose="pp-module-slot"
                                            className={
                                                ppBannerVariant
                                                    ? undefined
                                                    : 'discovery-unit-empty-render'
                                            }
                                        >
                                            {ppBannerVariant && (
                                                <PpBanner variant={ppBannerVariant} />
                                            )}
                                        </div>
                                    </TrackImpression>
                                ) : undefined
                            }
                            pageType={PAGE_TYPE_LOGGED_IN_HOMEPAGE}
                            getUnitComponentAndProps={getUnitComponentAndProps}
                            filter={discoveryContainerFilter}
                            itemIndicesByType={itemIndices}
                            fetchOptions={{
                                pageSize: 3,
                            }}
                        >
                            {!showPpModuleOverMessagingModule &&
                                lihpMarketingMessagingModulesExperiment && (
                                    <div data-item-index={2}>
                                        <MessagingModuleSlot
                                            variant={messagingModuleVariant}
                                            onImpression={handleMessagingModuleImpression}
                                        />
                                    </div>
                                )}
                        </DiscoveryUnitsContainer>
                    </AsyncPriceCourseCardContext.Provider>
                </Provider>
            </>
        );
    };

    const renderMyLearningUnit = () => {
        return (
            <div className="my-learning-wrapper component-margin">
                <FunnelLogContextProvider pageType={FUNNEL_LOG_PAGE_TYPE}>
                    <MyLearningUnitTabProvider learningTabs={[learningTab.current]}>
                        <MyLearningUnitContainer
                            title={gettext("Let's start learning")}
                            url={MY_COURSES_HOME_URL}
                            learningTabs={[learningTab.current]}
                            preloadedData={props.userEnrollments}
                        />
                    </MyLearningUnitTabProvider>
                </FunnelLogContextProvider>
            </div>
        );
    };

    const renderPopularCategoriesModule = () => {
        return (
            <div className="component-margin">
                <TopCategoriesComponent
                    categoryUniqueLearnerStats={dynamicCategoryUniqueLearnerStats}
                />
            </div>
        );
    };

    const renderTop10CoursesModule = () => {
        const enableTop10CoursesJapan =
            top10CoursesJapanExperiment?.configuration?.enableTop10CoursesJapan ?? false;

        if (!enableTop10CoursesJapan) return null;

        triggerImpressionEvent(top10CoursesJapanExperiment);

        return (
            <div className={classNames('component-margin', styles.bleed)}>
                <Top10CoursesComponent />
            </div>
        );
    };

    const renderStreaksUnit = () => {
        if (streaksStore.errorState) {
            return null;
        }
        return (
            <div className="component-margin">
                {streaksStore.isLoading ? (
                    <TextSkeleton withTitle={true} lineCountPerParagraph={2} />
                ) : streaksStore.streakData ? (
                    <StreaksBox streakData={streaksStore.streakData} />
                ) : null}
            </div>
        );
    };

    useEffect(() => {
        const {showStreaksOnMXLIHP: showUserStreaks} =
            userStreakOnMXLIHPExperiment?.configuration ?? {};
        setShowUserStreaks(showUserStreaks);
        if (showUserStreaks) {
            streaksStore.load();
        }
    }, [streaksStore, userStreakOnMXLIHPExperiment]);

    return (
        <OnePaneLayoutContainer>
            {/* The user claiming the retention promo may be logged into an account without
                the subscription; withRouteGuard redirects them here from /personal/home/. */}
            <RetentionPromoClaimAlert />
            <OnePaneLayoutFullWidthSection>
                <UserOccupationGoalHeader
                    userOccupationInfo={props.userOccupationInfo}
                    hasSeenHeader={props.hasSeenUserGoalHeader}
                    showBackground={false}
                />
                <PersonalPlanUpsellBanner />
                <GwgSection position="top" accelerator={props.accelerator} />
            </OnePaneLayoutFullWidthSection>
            <div className={classNames('ud-page-wrapper', styles['page-wrapper-no-top-padding'])}>
                <PauseSubscriptionBanner enrollment={pauseSubscriptionEnrollment} />
                {showCourseGuidanceFeature.show_course_guidance_feature && (
                    <div className="component-margin">
                        <CourseGuidanceEntryPointWithPreselection />
                    </div>
                )}
                <EntryPointPopUp />

                {renderTop10CoursesModule()}
                {isPriceCountryJapan && renderPopularCategoriesModule()}

                {renderMyLearningUnit()}

                {showUserStreaks && learningTab.current.hasContent && renderStreaksUnit()}

                {renderCertificationVoucherRecoBanner()}

                <div id={COURSE_RECOMMENDATIONS_SECTION_ID} className="component-margin">
                    <h2 className={classNames('ud-heading-serif-xxl', styles['section-title'])}>
                        {gettext('What to learn next')}
                    </h2>
                    {renderDiscoveryUnits()}
                    <GwgSection position="bottom" accelerator={props.accelerator} />
                </div>
            </div>
        </OnePaneLayoutContainer>
    );
});
